import { useMemo } from "react";
import type { Nullable } from "../../@types/generic";
import type {
    ForecastTrackingParameters,
    GenericTrackingParameters,
} from "../../@types/tracking";
import { formatTemperature } from "../../helpers/formatters/temperature";
import { numberToString } from "../../helpers/formatters/values";
import { usePageParams } from "../../hooks/usePageParams";
import { useLivecam } from "../../network/matlocq/queries/Livecam";
import { useLocation } from "../../network/matlocq/queries/Location";
import { useNCastNearby } from "../../network/matlocq/queries/NCastNearby";
import { usePollenNearby } from "../../network/matlocq/queries/PollenNearby";

export const useTrackingParameter = (): Nullable<
    ForecastTrackingParameters | GenericTrackingParameters | undefined
> => {
    const pageParams = usePageParams();
    const { pageType } = pageParams;
    const { data: location } = useLocation(
        pageType?.isAnyForecastPage && pageParams.code ? pageParams.code : null
    );
    const { data: livecam } = useLivecam(
        pageType?.isLivecamDetailPage && pageParams.id ? pageParams.id : null
    );
    const coordinates = pageType?.isAnyForecastPage
        ? {
              latitude: location?.coordinates.latitude,
              longitude: location?.coordinates.longitude,
          }
        : pageType?.isLivecamDetailPage
          ? {
                latitude: livecam?.coordinates.latitude,
                longitude: livecam?.coordinates.longitude,
            }
          : { latitude: null, longitude: null };
    const { data: nCast, isLoading: isLoadingNCast } = useNCastNearby({
        latitude: coordinates.latitude,
        longitude: coordinates.longitude,
        enabled: pageType?.isAnyForecastPage || pageType?.isLivecamDetailPage,
    });
    const { data: pollen, isLoading: isLoadingPollen } = usePollenNearby({
        latitude: coordinates.latitude,
        longitude: coordinates.longitude,
        enabled: pageType?.isAnyForecastPage,
    });

    const isWaitingForPollenData =
        isLoadingPollen && pageType?.isAnyForecastPage;
    const isWaitingForNCastData =
        isLoadingNCast &&
        (pageType?.isAnyForecastPage || pageType?.isLivecamDetailPage);

    return useMemo(() => {
        if (isWaitingForPollenData || isWaitingForNCastData) {
            return undefined;
        }

        const todaySummary = nCast?.forecast.dailyForecasts[0]?.summary ?? null;
        if (pageType?.isLivecamDetailPage) {
            return {
                vhs: "",
                wx_tmax:
                    todaySummary !== null
                        ? formatTemperature(todaySummary.temperature.max, true)
                        : "k.A.",
                wx_tmin:
                    todaySummary !== null
                        ? formatTemperature(todaySummary.temperature.min, true)
                        : "k.A.",
                wx_wx:
                    todaySummary !== null
                        ? numberToString(todaySummary.weatherAggregated.state)
                        : "",
                wx_cold:
                    todaySummary !== null
                        ? numberToString(todaySummary.weatherAggregated.state)
                        : "",
                wx_windmax:
                    todaySummary !== null
                        ? numberToString(todaySummary.windchill.max)
                        : "",
                wx_windmin:
                    todaySummary !== null
                        ? numberToString(todaySummary.windchill.min)
                        : "",
                wx_gust:
                    todaySummary !== null
                        ? numberToString(todaySummary.wind.gusts.value)
                        : "",
                w_ra:
                    todaySummary !== null
                        ? numberToString(todaySummary.prec.probability)
                        : "",
            };
        } else if (pageType?.isAnyForecastPage) {
            let vhs = "";

            if (pageType.isForecastMultiplePage) {
                vhs = "16-Tage Trend";
                if (pageType.isForecastWeekendPage) {
                    vhs = "Wochenende";
                } else if (pageParams.daysMultiple === "7") {
                    vhs = "7-Tage Prognose";
                } else if (pageParams.daysMultiple === "3") {
                    vhs = "3-Tage Übersicht";
                }
            } else if (pageType.isForecastTodayPage) {
                vhs = "heute";
            } else if (pageType.isForecastTomorrowPage) {
                vhs = "Morgen";
            } else {
                vhs = `in ${pageParams.daysSingle ?? "unknown"} Tagen`;
            }

            const dailyPollenIndices =
                pollen?.daily[0]?.pollenForecast.map((pf) => pf.pollenIndex) ??
                null;

            return {
                wx_cityname: location?.name ?? "",
                wx_postcode: location?.zipcodes[0] ?? "",
                wx_statecode: location?.admin1.location.code ?? "",
                postcodes: location?.zipcodes ?? [""],
                countryName: location?.country.code ?? "",
                continentName: location?.continent.location.name ?? "",
                vhs,
                wx_tmax:
                    todaySummary !== null
                        ? formatTemperature(todaySummary.temperature.max, true)
                        : "k.A.",
                wx_tmin:
                    todaySummary !== null
                        ? formatTemperature(todaySummary.temperature.min, true)
                        : "k.A.",
                wx_wx:
                    todaySummary !== null
                        ? numberToString(todaySummary.weatherAggregated.state)
                        : "",
                wx_cold:
                    todaySummary !== null
                        ? numberToString(todaySummary.weatherAggregated.state)
                        : "",
                wx_windmax:
                    todaySummary !== null
                        ? numberToString(todaySummary.windchill.max)
                        : "",
                wx_windmin:
                    todaySummary !== null
                        ? numberToString(todaySummary.windchill.min)
                        : "",
                wx_gust:
                    todaySummary !== null
                        ? numberToString(todaySummary.wind.gusts.value)
                        : "",
                w_ra:
                    todaySummary !== null
                        ? numberToString(todaySummary.prec.probability)
                        : "",
                wx_uv:
                    todaySummary !== null
                        ? numberToString(todaySummary.uvIndex.max)
                        : "",
                wx_pollen:
                    dailyPollenIndices !== null
                        ? numberToString(Math.max(...dailyPollenIndices))
                        : "",
            };
        } else {
            return null;
        }
    }, [
        nCast,
        pollen,
        isWaitingForPollenData,
        location,
        pageType,
        pageParams,
        isWaitingForNCastData,
    ]);
};
